import { chartTypeEnum } from '../constants';
import PropTypes from 'prop-types';
import { WordCloud } from '../WordCloud';
import { RadarChart } from '../RadarChart';
import { DoughnutChart } from '../DoughnutChart';
import { BarChart } from '../SimpleBar';
import { StackedBarChart } from '../StackedBarChart';
import { BarPlot, LinePlot, MonthLinePlot, PieChart } from '../PlotlyChart';
import NumberBox from '../chartComponents/NumberBox';
import TopHitsBox from '../chartComponents/TopHitsBox';
import { BarPercentageChart } from '../BarPercentageChart';
import { prepareChartCampaignData, prepareChartData } from '../../../shared/functions/chartDataProcessor';
import { BarVerticalChart } from '../BarVerticalChart';
import { BarWideChart } from '../BarWideChart';
import { LineChart } from '../LineChart';
import { useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { IconButton } from '@mui/material';
import html2canvas from 'html2canvas';
import { StackedBarVerticalChart } from '../StackedBarVerticalChart';
import { applyAnalyticsChartTypographyDefaults } from '../chartTypography';

applyAnalyticsChartTypographyDefaults();

function ChartContent({ chartDataParam, query, open, showCameraButton = true, showTitle = false }) {
  const useStyles = makeStyles(() => ({
    chartDiv: {
      width: '100%',
      height: '100%',
      minHeight: 0,
      padding: 0,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    chartInner: {
      width: '100%',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cameraButton: {
      position: 'absolute',
      top: open ? '5%' : '10%',
      right: '7%',
      zIndex: '1000',
    },
  }));

  const classes = useStyles();

  const chartRef = useRef();

  // wordcloud needs a bit of time to finish rendering before calling
  // html2canvas
  const printChart = async () => {
    const canvas = await html2canvas(chartRef.current, {});

    const data = canvas.toDataURL('image/jpg');
    const link = document.createElement('a');

    if (typeof link.download === 'string') {
      link.href = data;
      link.download = 'image.jpg';

      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      window.open(data);
    }
  };

  const cameraButton = showCameraButton ? (
    <IconButton className={classes.cameraButton} onClick={printChart}>
      <PhotoCameraIcon />
    </IconButton>
  ) : null;

  const title = chartDataParam.isYearlyChart
    ? `${chartDataParam.title} (${new URLSearchParams(query).get('year')})`
    : chartDataParam.title;
  // Team Analytics cards use the shared card header for titling; suppress any in-chart titles.
  const shouldRenderInternalTitle = Boolean(showTitle) && chartDataParam.analyticsVariant !== 'team';
  const renderedTitle = shouldRenderInternalTitle ? title : '';
  let paletteVariant = 'default';
  if (chartDataParam.analyticsVariant === 'custom') {
    paletteVariant = 'custom';
  } else if (chartDataParam.analyticsVariant === 'team') {
    paletteVariant = 'team';
  }

  let chart;
  switch (chartDataParam.type) {
    case chartTypeEnum.wordCloud:
      chart = (
        <WordCloud
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></WordCloud>
      );
      break;
    case chartTypeEnum.ChartJSRadar:
      chart = (
        <RadarChart
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
        ></RadarChart>
      );
      break;
    case chartTypeEnum.ChartJSDoughnut:
      chart = (
        <DoughnutChart
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></DoughnutChart>
      );
      break;
    case chartTypeEnum.ChartJSBar:
      chart = (
        <BarChart
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          label={chartDataParam.label}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></BarChart>
      );
      break;
    case chartTypeEnum.ChartJSStackedBar:
      chart = (
        <StackedBarChart
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          query={query}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></StackedBarChart>
      );
      break;
    case chartTypeEnum.pie:
      chart = (
        <PieChart
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          type={chartDataParam.type}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></PieChart>
      );
      break;
    case chartTypeEnum.numberBox:
      const nboxData = {
        thisMonth: chartDataParam.chartData[0].y[1],
        historical: chartDataParam.chartData[0].y[0],
        title: renderedTitle,
        subTitle: chartDataParam.subTitle,
        showTitle: shouldRenderInternalTitle,
      };
      chart = <NumberBox data={nboxData}></NumberBox>;
      break;
    case chartTypeEnum.bar:
      chart = (
        <BarPlot
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          type={chartDataParam.type}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></BarPlot>
      );
      break;
    case chartTypeEnum.topHits:
      chart = (
        <TopHitsBox
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          type={chartDataParam.type}
          showTitle={shouldRenderInternalTitle}
        ></TopHitsBox>
      );
      break;
    case chartTypeEnum.barPercentage:
      chart = (
        <BarPercentageChart
          chartData={prepareChartData(chartDataParam.chartData, paletteVariant)}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
        ></BarPercentageChart>
      );
      break;
    case chartTypeEnum.barVertical:
      chart = (
        <BarVerticalChart
          chartData={prepareChartData(chartDataParam.chartData, paletteVariant)}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
        ></BarVerticalChart>
      );
      break;
    case chartTypeEnum.barWide:
      chart = (
        <BarWideChart
          chartData={prepareChartCampaignData(chartDataParam.chartData, paletteVariant)}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
        ></BarWideChart>
      );
      break;
    case chartTypeEnum.stackedBarVertical:
      chart = (
        <StackedBarVerticalChart
          chartData={prepareChartCampaignData(chartDataParam.chartData, paletteVariant)}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
        ></StackedBarVerticalChart>
      );
      break;
    case chartTypeEnum.PlotlyMonthLine:
      chart = (
        <MonthLinePlot
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></MonthLinePlot>
      );
      break;

    case chartTypeEnum.line:
      chart = (
        <LinePlot
          chartData={chartDataParam.chartData}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
          paletteVariant={paletteVariant}
        ></LinePlot>
      );
      break;
    default:
      chart = (
        <LineChart
          chartData={prepareChartData(chartDataParam.chartData, paletteVariant)}
          title={renderedTitle}
          showTitle={shouldRenderInternalTitle}
        ></LineChart>
      );
      break;
  }

  return (
    <div className={classes.chartDiv} ref={chartRef}>
      {cameraButton}
      <div className={classes.chartInner}>{chart}</div>
    </div>
  );
}

ChartContent.propTypes = {
  chartDataParam: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chartData: PropTypes.any,
    label: PropTypes.string,
    subTitle: PropTypes.string,
    isYearlyChart: PropTypes.bool,
    analyticsVariant: PropTypes.string,
  }).isRequired,
  query: PropTypes.string,
  open: PropTypes.bool,
  showCameraButton: PropTypes.bool,
  showTitle: PropTypes.bool,
};

export default ChartContent;
